// src/components/Layout/Footer.js - CORRECTED VERSION
import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import './Footer.css';

const Footer = () => {
  const [shouldHide, setShouldHide] = useState(false);

  // Detect if footer should be hidden
  useEffect(() => {
    const checkFooterVisibility = () => {
      const hasPlaygroundClass = document.body.classList.contains('playground-active');
      const isMobile = window.innerWidth <= 991;

      // Hide footer only when both conditions are met
      setShouldHide(hasPlaygroundClass && isMobile);
    };

    // Check immediately
    checkFooterVisibility();

    // Listen for class changes on body using MutationObserver
    const observer = new MutationObserver(() => {
      checkFooterVisibility();
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Listen for resize events
    const handleResize = () => {
      checkFooterVisibility();
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Don't render footer if it should be hidden
  if (shouldHide) {
    return null;
  }

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <motion.footer
      className="glass-footer"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {/* Animated background particles - hide on mobile for performance */}
      {!isMobile && (
        <div className="footer-particles">
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="footer-particle"
              initial={{
                opacity: 0,
                x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 800),
                y: Math.random() * 100,
              }}
              animate={{
                opacity: [0, 0.4, 0],
                x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 800),
                y: Math.random() * 100,
              }}
              transition={{
                duration: 20 + Math.random() * 10,
                repeat: Infinity,
                delay: Math.random() * 5,
              }}
            />
          ))}
        </div>
      )}

      <Container>
        <div className="footer-content">
          {/* Logo and Brand Section */}
          <motion.div
            className="footer-brand"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <div className="footer-logo-container">
              <motion.img
                src="/images/logo.png"
                alt="Banking Intelligence Logo"
                className="footer-logo"
                whileHover={!isMobile ? { rotate: 360 } : {}}
                transition={{ duration: 1 }}
              />
            </div>
            <div className="footer-brand-text">
              <h5 className="footer-title">Banking Intelligence</h5>
              <p className="footer-subtitle">API Platform</p>
            </div>
          </motion.div>

          {/* Status Section - Show on tablet and up */}
          {!isMobile && (
            <motion.div
              className="footer-status"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <h6 className="footer-status-title">System Status</h6>
              <div className="status-indicators-footer">
                <div className="status-item-footer">
                  <div className="status-dot-footer status-online-footer"></div>
                  <span className="status-text-footer">API Online</span>
                </div>
                <div className="status-item-footer">
                  <div className="status-dot-footer status-online-footer"></div>
                  <span className="status-text-footer">All Systems Operational</span>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Bottom Section */}
        <motion.div
          className="footer-bottom"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="footer-divider"></div>
          <div className="footer-copyright">
            <p className="copyright-text">
              &copy; {new Date().getFullYear()}{' '}
              <motion.a
                href="https://vivytech.com"
                className="company-link"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                VIVY TECH USA INC.
              </motion.a>{' '}
              All rights reserved.
            </p>

            {/* Social Links */}
            <div className="social-links">
              {[
                { icon: 'bi-github', href: '#' },
                { icon: 'bi-twitter', href: '#' },
                { icon: 'bi-linkedin', href: '#' }
              ].map((social, index) => (
                <motion.a
                  key={social.icon}
                  href={social.href}
                  className="social-link"
                  whileHover={!isMobile ? {
                    scale: 1.2,
                    rotate: index % 2 === 0 ? 10 : -10
                  } : {}}
                  whileTap={{ scale: 0.9 }}
                >
                  <i className={`bi ${social.icon}`}></i>
                </motion.a>
              ))}
            </div>
          </div>
        </motion.div>
      </Container>

      {/* Glow effect - disabled on mobile for performance */}
      {!isMobile && (
        <div className="footer-glow" id="footer-glow"></div>
      )}
    </motion.footer>
  );
};

export default Footer;